import { useState, useEffect, useCallback, Suspense, lazy } from 'react'
import { AuthProvider } from './contexts/AuthContext'
import { trackPageView } from './lib/analytics.js'
import { db } from './firebase/config'
import * as fsApi from 'firebase/firestore'
import { hydrateProductPrices } from './config/products.js'

const RaioXPage = lazy(() => import('./pages/RaioXPage'))
const SuccessPage = lazy(() => import('./components/SuccessPage'))
const PrivacidadePage = lazy(() => import('./pages/LegalPages').then(module => ({ default: module.PrivacidadePage })))
const TermosPage = lazy(() => import('./pages/LegalPages').then(module => ({ default: module.TermosPage })))
const ReembolsoPage = lazy(() => import('./pages/LegalPages').then(module => ({ default: module.ReembolsoPage })))

function resolveRoute(pathname) {
  // Remove query string if present
  const pathWithoutQuery = pathname.split('?')[0]
  // Remove trailing slashes and normalize
  const cleanPath = pathWithoutQuery.replace(/\/$/, '') || '/'
  
  if (cleanPath === '/') return 'picker'
  if (cleanPath === '/veiculos') return 'veiculos'
  if (cleanPath === '/imoveis') return 'imoveis'
  if (cleanPath === '/raiox') return 'raiox'
  if (cleanPath === '/sucesso') return 'sucesso'
  if (cleanPath === '/privacidade') return 'privacidade'
  if (cleanPath === '/termos') return 'termos'
  if (cleanPath === '/reembolso') return 'reembolso'
  return 'picker'
}

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
      <style>{`
        .loader-circle { border: 4px solid #E2E8F0; border-left-color: #1D4ED8; border-radius: 50%; width: 48px; height: 48px; animation: spin 1s linear infinite; }
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      `}</style>
      <div className="loader-circle"></div>
    </div>
  )
}

function AppContent() {
  const [route, setRoute] = useState(() => resolveRoute(window.location.pathname))

  const navigate = useCallback((path) => {
    window.history.pushState({}, '', path)
    setRoute(resolveRoute(path))
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
    trackPageView(path)
  }, [])

  useEffect(() => {
    const onPop = () => {
      const path = window.location.pathname
      setRoute(resolveRoute(path))
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
      trackPageView(path)
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])
  
  useEffect(() => {
    hydrateProductPrices(db, fsApi).catch(err => console.error('Failed to hydrate prices:', err))
  }, [])

  useEffect(() => {
    // Global link interceptor for internal navigation
    const onClick = (e) => {
      const anchor = e.target.closest('a[href]')
      if (!anchor) return
      const href = anchor.getAttribute('href')
      if (!href || href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('tel:') || anchor.target === '_blank') return
      if (href.startsWith('/')) {
        e.preventDefault()
        navigate(href)
      }
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [navigate])

  const renderRoute = () => {
    if (route === 'sucesso') {
      const params = new URLSearchParams(window.location.search)
      return <SuccessPage leadId={params.get('leadId')} leadCollection={params.get('leadCollection')} />
    }
    if (route === 'privacidade') return <PrivacidadePage navigate={navigate} />
    if (route === 'termos') return <TermosPage navigate={navigate} />
    if (route === 'reembolso') return <ReembolsoPage navigate={navigate} />

    if (route === 'veiculos') return <RaioXPage key="veiculos" navigate={navigate} initialType="veiculo" />
    if (route === 'imoveis') return <RaioXPage key="imoveis" navigate={navigate} initialType="imovel" />
    
    // Default (landing page)
    return <RaioXPage key="landing" navigate={navigate} />
  }

  return (
    <Suspense fallback={<PageLoader />}>
      {renderRoute()}
    </Suspense>
  )
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  )
}
